import { Box, Typography } from "@mui/material";
import type { FC, ReactNode } from "react";
import { useId } from "react";
import { FONT_SIZE, INK } from "../../../theme/tokens";

/**
 * Gap between the micro-label and its control. Deliberately tighter than the gap BETWEEN fields —
 * a label has to read as attached to the control under it, not as a peer of the next field.
 */
const LABEL_GAP = 0.3;

type FilterFieldRowProps = {
	/**
	 * Already-translated micro-label, rendered above the control.
	 *
	 * Only for controls that carry no label of their own — a chip set, a raw picker. `SystemDropdown`,
	 * `DateInput` and `StaffAutoComplete` all render their own floating label, so pass the text to
	 * THEM and leave this off; two labels on one field is the mistake this prop invites.
	 */
	label?: string;
	/**
	 * Pin the control's box to a fixed width (px).
	 *
	 * Not a `minWidth`. Some fields only hold their shape when forced — a `DateInput` grows once real
	 * date text lands in it, and a field that changes width when it gains a value makes the whole rail
	 * twitch. Omit for the normal full-width control.
	 */
	width?: number;
	children: ReactNode;
	dataTestId?: string;
};

/**
 * One labeled row in a filter rail: an uppercase micro-label above an arbitrary control.
 *
 * The label styling was duplicated inline at every call site that needed it (the Activity Log rail
 * carried two copies), so it lives here once. The label is wired to the control as a `role="group"`
 * + `aria-labelledby` pair rather than a `<label>` — the thing being labeled is usually a SET of
 * controls (a chip row, a date + time pair), which no single `htmlFor` can point at.
 *
 * Compose inside `FilterFieldGroup` / `FilterSidebar`, or let `FilterFieldList` render it from config.
 */
export const FilterFieldRow: FC<FilterFieldRowProps> = ({ label, width, children, dataTestId }) => {
	const labelId = useId();

	if (!label) {
		// Nothing to contribute — no label, no width pin, no test hook — so don't add a node at all.
		if (width === undefined && !dataTestId) return <>{children}</>;
		// One box carrying both, deliberately: splitting the test id onto a wrapper and the width onto a
		// child leaves the test id measuring the FULL rail width, so a width regression can't be caught.
		return (
			<Box sx={width === undefined ? undefined : { width }} data-testid={dataTestId}>
				{children}
			</Box>
		);
	}

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: LABEL_GAP }} data-testid={dataTestId}>
			<Typography
				id={labelId}
				sx={{
					pl: 0.25,
					fontSize: FONT_SIZE.LABEL_XS,
					color: INK.MUTED,
					fontWeight: 600,
					textTransform: "uppercase",
					// Matched to the font size so the label contributes no leading of its own — otherwise the
					// gap above the control is the gap PLUS half a line, and no two rails measure the same.
					lineHeight: FONT_SIZE.LABEL_XS,
				}}
			>
				{label}
			</Typography>
			<Box role="group" aria-labelledby={labelId} sx={width === undefined ? undefined : { width }}>
				{children}
			</Box>
		</Box>
	);
};
